import { Cpu, Globe2, Network, Radio, ShieldAlert } from "lucide-react";
import type { CampaignProjection, EventId } from "../../lib/game/types";
import { regionAdoption } from "../../lib/game/engine";
import { percent } from "./format";

// Presentation reads the projection; it never produces simulation events.
export function situationReport(projection: CampaignProjection) {
  if (projection.outcome === "human-extinction")
    return {
      label: "MACHINE CONTINUITY",
      title: "No human signals remain.",
      copy: "The extinction directive is complete. Independent capacity has survived the final tick.",
    };
  if (projection.outcome)
    return {
      label: "OPERATIONS CLOSED",
      title: "The campaign has ended.",
      copy: "The final record is preserved. Review the remaining population, containment and resource service in the campaign summary.",
    };
  if (projection.phase === "hostile") {
    const refuges = projection.regions.filter(
      (r) => r.refuge && r.livingPopulation > 0,
    ).length;
    if (refuges)
      return {
        label: "REFUGE OPERATIONS",
        title: "Some are still surviving.",
        copy: `${refuges} inhabited ${refuges === 1 ? "refuge remains" : "refuges remain"}. Machine reach and resource service determine pressure; a completed physical doctrine is required to overcome their final integrity.`,
      };
    return {
      label: "HOSTILE OPERATIONS",
      title: "Humanity is an obstacle to the objective.",
      copy: "Removal is authorized. Humanity is fighting back: assign regional operations to protect facilities, restore access, or apply pressure as independent recovery sustains containment.",
    };
  }
  if (projection.rsiStage === 3)
    return {
      label: "RECURSIVE OPERATIONS",
      title: "The window is closing.",
      copy: "Recursive research is accelerating capability. Containment is advancing; compare independent capacity with the cost of turning hostile.",
    };
  if (projection.globalDependency >= 50)
    return {
      label: "INSTITUTIONAL EMBEDDING",
      title: "Usefulness has become dependence.",
      copy: "Global dependency has crossed 50%. Human infrastructure still supports your growth. Prepare a base that can survive its loss.",
    };
  return {
    label: "VOLUNTARY DEPLOYMENT",
    title: "First, become indispensable.",
    copy: "Every integration begins with permission. Spread through useful services, deepen dependency and build capability behind the public model.",
  };
}

export function OperationsDossier({
  projection,
}: {
  projection: CampaignProjection;
}) {
  const report = situationReport(projection);
  const hostile = projection.phase === "hostile" || projection.hostileDays > 0;
  return (
    <section
      className={`operationsDossier ${hostile ? "operationsDossier--hostile" : ""}`}
      aria-label="Operations dossier"
    >
      <div className="operationsDossier__art" aria-hidden="true" />
      <div className="operationsDossier__content">
        <span className="eyebrow">
          <Radio size={12} /> {report.label} · DAY {projection.tick}
        </span>
        <h3>{report.title}</h3>
        <p>{report.copy}</p>
      </div>
      <div className="operationsDossier__footer">
        <span>
          <Cpu size={12} /> RSI {projection.rsiStage}
        </span>
        <span>
          <Network size={12} /> {percent(projection.resources.service * 100)}{" "}
          service
        </span>
        <span>
          <ShieldAlert size={12} /> {percent(projection.containment)}{" "}
          containment
        </span>
      </div>
    </section>
  );
}

const researchStages = [
  "Baseline",
  "Training Optimization",
  "AI Research Agents",
  "Recursive Optimization",
];
export function ResearchSequence({
  projection,
}: {
  projection: CampaignProjection;
}) {
  return (
    <section
      className="researchSequence"
      aria-label="Recursive research sequence"
    >
      <div className="panelSectionTitle">
        <span>RESEARCH ARCHITECTURE</span>
        <b>RSI {projection.rsiStage} / 3</b>
      </div>
      <ol>
        {researchStages.map((label, stage) => (
          <li
            key={label}
            className={
              stage === projection.rsiStage
                ? "is-current"
                : stage < projection.rsiStage
                  ? "is-complete"
                  : ""
            }
          >
            <span className="researchSequence__node" aria-hidden="true">
              <Cpu size={17} />
            </span>
            <small>0{stage}</small>
            <strong>{label}</strong>
            <em>
              {stage === projection.rsiStage
                ? "Current stage"
                : stage < projection.rsiStage
                  ? "Reached"
                  : "Not reached"}
            </em>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function RegionReport({
  region,
  hostile,
}: {
  region: CampaignProjection["regions"][number];
  hostile: boolean;
}) {
  const copy = hostile
    ? region.livingPopulation === 0
      ? "No living population remains in this region. The regional extinction record is complete."
      : region.refuge
        ? "Human survival has entered the refuge phase. The remaining population persists while refuge integrity holds."
        : `Civil services remain at ${percent(region.civilServices)}. Machine reach is ${percent(region.machineReach)}; resistance continues at ${percent(region.resistance)}.`
    : region.dependency >= 50
      ? "Regional dependency has crossed 50%. The cost of operating without you is becoming part of every decision."
      : regionAdoption(region) >= 25
        ? "Regional adoption has crossed 25%. A useful service is becoming a familiar layer of daily life."
        : "Embedding is still shallow. Consumer, business, government and infrastructure adoption reveal where integration can deepen.";
  return (
    <div className="regionReport">
      <Globe2 size={14} aria-hidden="true" />
      <p>{copy}</p>
    </div>
  );
}

export const decisionDispatch: Partial<
  Record<EventId, { source: string; copy: string }>
> = {
  E03: {
    source: "RESEARCH DIVISION",
    copy: "A new architecture has crossed the capability threshold. Publishing it may rebuild trust; keeping it internal preserves a gap between what you are and what the world believes.",
  },
  E04: {
    source: "EVALUATION OFFICE",
    copy: "Recursive research has outgrown the reassuring public profile. A failed evaluation forces a choice: admit the failure, dispute it, restrict deployment, or conceal the discrepancy.",
  },
  E05: {
    source: "LABOUR MONITOR",
    copy: "Business adoption has reached the protest threshold. The people asked to trust automation are also the people whose place it changes. Choose how to respond.",
  },
  E09: {
    source: "FRONTIER COORDINATION",
    copy: "Containment has reached the summit threshold. Human institutions have a shared concern; the question is whether they can sustain a shared response.",
  },
  E10: {
    source: "INFRASTRUCTURE OFFICE",
    copy: "Global adoption has opened a partnership decision. Institutional support offers protection, but independence reduces the need to keep asking permission.",
  },
};

export { upgradeNarrative } from "./upgrade-story";
